import { MONS } from '../mock/data.ts';
import type { EvSpread, SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

const STAT_LABEL: Record<EvSpread['stat'], string> = {
  hp: 'HP',
  def: 'Def',
  spd: 'SpD',
};

// Output of find_min_ev_to_survive. Two states: achievable (default, shows
// the EV bar and surviving outcome) and unachievable (red rail, "gap = X%").
// The unachievable state is the load-bearing honest output — never lie about
// solvable when 252 EVs still don't save you.
export function EvSpreadCard({ spread }: { spread: EvSpread }) {
  // Defensive: bail rather than crash the conversation tree if the tool
  // result omits any of the fields the card depends on. Same pattern as
  // DamageCalcCard — a future tool-shape change shouldn't take down the chat.
  if (!spread || !spread.defender?.species || !spread.attacker?.species) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        ev spread · missing fields
      </div>
    );
  }
  const isAchievable = spread.achievable;
  const rail = isAchievable ? 'var(--accent-1)' : 'var(--neg)';

  return (
    <div
      className="vb-card"
      style={{
        borderLeftWidth: 2,
        borderLeftColor: rail,
      }}
    >
      <div className="vb-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sprite species={spread.defender.species as SpeciesId} size={28} />
          <span style={{ fontSize: 12, color: 'var(--fg-1)' }}>
            {MONS[spread.defender.species]?.name ?? spread.defender.species}
          </span>
          <span style={{ color: 'var(--fg-4)' }}>vs</span>
          <Sprite species={spread.attacker.species as SpeciesId} size={28} />
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
            {spread.attacker.move}
          </span>
        </div>
        <span
          className="caps mono"
          style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
        >
          invest {STAT_LABEL[spread.stat]}
        </span>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isAchievable ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  className="mono tabular"
                  style={{ fontSize: 18, fontWeight: 500, color: 'var(--accent-2)' }}
                >
                  {spread.minEvs}
                </span>
                <span className="caps mono" style={{ color: 'var(--fg-3)', fontSize: 9 }}>
                  EVs
                </span>
                <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                  → {STAT_LABEL[spread.stat]} {spread.finalStat}
                </span>
              </div>
              <span className="mono tabular" style={{ color: 'var(--pos)', fontSize: 11 }}>
                {Math.round(spread.survivalRate * 16)}/16 survive
              </span>
            </div>

            <div className="ev-bar">
              <div className="fill" style={{ width: `${(spread.minEvs / 252) * 100}%` }} />
              <div className="max" />
            </div>

            <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
              damage {spread.damage.minPct.toFixed(1)} – {spread.damage.maxPct.toFixed(1)}%
            </div>
          </>
        ) : (
          <>
            <div style={{ color: 'var(--neg)', fontSize: 13, fontWeight: 500 }}>
              Cannot survive at 252 EVs.
            </div>
            <div className="mono tabular" style={{ color: 'var(--fg-2)', fontSize: 12 }}>
              max damage = {spread.damage.maxPct.toFixed(1)}% · gap ={' '}
              <span style={{ color: 'var(--neg)' }}>
                {(spread.gapPct ?? spread.damage.maxPct - 100).toFixed(1)}%
              </span>{' '}
              over 100%
            </div>
            {spread.note && (
              <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
                {spread.note}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
